/**
 * Anant Vyapar — Admin Panel
 * Shows platform-wide subscriptions, DFY leads, defaulters, and export blocking.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Store, Users, Award, ShieldOff, RefreshCw, Ban } from 'lucide-react';
import api from '../../utils/api';

export default function VyaparAdmin() {
  const [tab, setTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [subs, setSubs] = useState([]);
  const [dfy, setDfy] = useState([]);
  const [defaulters, setDefaulters] = useState([]);

  const load = async () => {
    try {
      const [o, s, d, def] = await Promise.all([
        api.get('/vyapar/admin/overview'),
        api.get('/vyapar/admin/subscriptions'),
        api.get('/vyapar/admin/dfy-leads'),
        api.get('/vyapar/admin/defaulters'),
      ]);
      setOverview(o.data); setSubs(s.data.subscriptions); setDfy(d.data.leads); setDefaulters(def.data.defaulters);
    } catch (e) { toast.error('Load failed'); }
  };
  useEffect(() => { load(); }, []);

  const setDFYStatus = async (id, status) => {
    await api.put(`/vyapar/admin/dfy-leads/${id}`, { status });
    toast.success('Updated');
    load();
  };

  const toggleExport = async (userId, block) => {
    await api.post(`/vyapar/admin/users/${userId}/block-export`, { block });
    toast.success(block ? 'Blocked' : 'Unblocked');
  };

  return (
    <div className="min-h-screen pt-20 pb-12 px-4 bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-white" data-testid="vyapar-admin">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-fuchsia-400 text-xs font-bold uppercase tracking-widest">Admin · Anant Smart Vyapar System</div>
            <h1 className="text-3xl font-extrabold">Vyapar Control Panel</h1>
          </div>
          <div className="flex gap-2">
            <button onClick={async () => {
              const src = prompt('Source workspace ID (dekh sakte ho subscriptions tab me):');
              if (!src) return;
              const tgt = prompt('Target user ID (kis user ke account me clone karna hai):');
              if (!tgt) return;
              const name = prompt('Naye workspace ka naam (blank = "<original> (Clone)"):', '');
              try {
                const r = await api.post('/vyapar/admin/workspace/clone', {
                  source_workspace_id: src, target_user_id: tgt,
                  new_business_name: name || null,
                  include_customers: true, include_stock: true, include_custom_fields: true,
                  include_invoices: window.confirm('Invoices bhi clone karein? (OK = Yes)'),
                });
                toast.success(`✅ Cloned to @${r.data.target_user}. Items: ${JSON.stringify(r.data.cloned)}`);
              } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
            }} data-testid="vadm-clone-btn" className="px-3 py-1.5 rounded-lg bg-cyan-500 text-white text-xs font-bold flex items-center gap-1">
              🧬 Clone Workspace
            </button>
            <button onClick={load} className="p-2 rounded-lg bg-white/5 hover:bg-white/10"><RefreshCw className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="flex gap-2 mb-4 overflow-x-auto">
          {['overview','subscriptions','dfy','defaulters'].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`vadm-tab-${t}`} className={`px-3 py-1.5 rounded-lg text-xs font-bold capitalize ${tab === t ? 'bg-fuchsia-500 text-white' : 'bg-white/5 text-slate-300'}`}>{t}</button>
          ))}
        </div>
        {tab === 'overview' && overview && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="vadm-overview">
            <StatBox label="Workspaces" value={overview.workspaces} icon={Store} />
            <StatBox label="Customers" value={overview.customers} icon={Users} />
            <StatBox label="Invoices" value={overview.invoices} icon={Award} />
            <StatBox label="Active Subs" value={overview.active_subscriptions} icon={Award} />
            <StatBox label="Active Trials" value={overview.active_trials} icon={Award} accent="text-emerald-300" />
            <StatBox label="DFY Pending" value={overview.dfy_pending} icon={Award} accent="text-amber-300" />
            <StatBox label="Network Defaulters" value={overview.network_defaulters} icon={ShieldOff} accent="text-red-300" />
          </div>
        )}
        {tab === 'subscriptions' && (
          <div className="rounded-xl border border-white/10 overflow-hidden" data-testid="vadm-subs">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-xs uppercase text-slate-400"><tr><th className="text-left px-3 py-2">User</th><th className="text-left px-3 py-2">Plan</th><th className="text-right px-3 py-2">AT</th><th className="px-3 py-2">Expires</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Export</th></tr></thead>
              <tbody>
                {subs.map((s) => (
                  <tr key={s.id} className="border-t border-white/5">
                    <td className="px-3 py-2"><div className="font-bold">{s.user?.full_name || s.user?.username}</div><div className="text-[11px] text-slate-400">{s.user?.email}</div></td>
                    <td className="px-3 py-2">{s.plan_name}</td>
                    <td className="px-3 py-2 text-right">{s.price_at}</td>
                    <td className="px-3 py-2 text-xs">{new Date(s.expires_at).toLocaleDateString()}</td>
                    <td className="px-3 py-2"><span className={`text-[10px] px-2 py-0.5 rounded-full ${s.status === 'active' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-500/20 text-slate-400'}`}>{s.status}</span></td>
                    <td className="px-3 py-2 flex gap-1">
                      <button onClick={() => toggleExport(s.user_id, true)} data-testid={`vadm-block-${s.user_id}`} className="text-red-300 text-[10px] font-bold flex items-center gap-1"><Ban className="w-3 h-3" /> Block</button>
                      <button onClick={() => toggleExport(s.user_id, false)} className="text-emerald-300 text-[10px] font-bold ml-1">Unblock</button>
                    </td>
                  </tr>
                ))}
                {subs.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-slate-500 text-xs">Koi subscription nahi.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        {tab === 'dfy' && (
          <div className="rounded-xl border border-white/10 overflow-hidden" data-testid="vadm-dfy">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-xs uppercase text-slate-400"><tr><th className="text-left px-3 py-2">User</th><th className="text-left px-3 py-2">Mobile</th><th className="text-left px-3 py-2">Plan</th><th className="px-3 py-2">Date</th><th className="px-3 py-2">Status</th></tr></thead>
              <tbody>
                {dfy.map((l) => (
                  <tr key={l.id} className="border-t border-white/5">
                    <td className="px-3 py-2"><div className="font-bold">{l.user?.full_name || l.user?.username}</div><div className="text-[11px] text-slate-400">{l.user?.email}</div></td>
                    <td className="px-3 py-2">{l.user?.mobile || '—'}</td>
                    <td className="px-3 py-2">{l.plan_id}</td>
                    <td className="px-3 py-2 text-xs">{new Date(l.created_at).toLocaleDateString()}</td>
                    <td className="px-3 py-2 flex gap-1">
                      {['pending','in_progress','done'].map((s) => (
                        <button key={s} onClick={() => setDFYStatus(l.id, s)} data-testid={`vadm-dfy-${s}-${l.id}`} className={`text-[10px] px-2 py-0.5 rounded ${l.status === s ? 'bg-fuchsia-500 text-white font-bold' : 'bg-white/5 text-slate-300'}`}>{s}</button>
                      ))}
                    </td>
                  </tr>
                ))}
                {dfy.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-slate-500 text-xs">Koi DFY lead nahi.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        {tab === 'defaulters' && (
          <div className="rounded-xl border border-white/10 overflow-hidden" data-testid="vadm-defaulters">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-xs uppercase text-slate-400"><tr><th className="text-left px-3 py-2">Mobile</th><th className="text-left px-3 py-2">Customer</th><th className="text-left px-3 py-2">Reported By</th><th className="text-left px-3 py-2">Reason</th><th className="px-3 py-2">Date</th></tr></thead>
              <tbody>
                {defaulters.map((d) => (
                  <tr key={d.mobile} className="border-t border-white/5">
                    <td className="px-3 py-2 font-mono">{d.mobile}</td>
                    <td className="px-3 py-2">{d.customer_name}</td>
                    <td className="px-3 py-2">{d.workspace_name}</td>
                    <td className="px-3 py-2 text-xs">{d.reason || '—'}</td>
                    <td className="px-3 py-2 text-xs text-slate-400">{new Date(d.marked_at).toLocaleDateString()}</td>
                  </tr>
                ))}
                {defaulters.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-slate-500 text-xs">Network par koi defaulter nahi.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const StatBox = ({ label, value, icon: Icon, accent = 'text-fuchsia-300' }) => (
  <div className="rounded-xl border border-white/10 bg-slate-900/50 p-3">
    <div className="flex items-center gap-2 mb-1"><Icon className={`w-4 h-4 ${accent}`} /><span className="text-[11px] uppercase text-slate-400">{label}</span></div>
    <div className="text-2xl font-extrabold">{value}</div>
  </div>
);
